import dayjs from 'dayjs';

/**
 * Safely converts a value to a number, returning 0 if the value is null, undefined, or NaN
 * @param {*} value - Value to convert to number
 * @returns {number} - Safe number value
 */
export const safeNumber = (value) => {
  if (value === null || value === undefined) return 0;
  const num = Number(value);
  return isNaN(num) ? 0 : num;
};

/**
 * Ensures percentage is between 0 and 100
 * @param {number} percentage - Percentage value to normalize
 * @returns {number} - Normalized percentage between 0-100
 */
const normalizePercentage = (percentage) => {
  const validPercentage = safeNumber(percentage);
  return Math.min(Math.max(validPercentage, 0), 100);
};

/**
 * Rounds a number to 2 decimal places
 * @param {number} num - The number to round
 * @returns {number} The rounded number with 2 decimal places
 */
export const roundTo2Decimals = (num) =>
  Number((Math.round(num * 100) / 100).toFixed(2));

/**
 * Formats a number to a string with 2 decimal places
 * @param {number} num - The number to format
 * @returns {string} The formatted number with 2 decimal places
 */
export const formatDecimal = (num) => safeNumber(num).toFixed(2);

/**
 * Calculates total hours from an array of records with clock in/out times
 * @param {Array<{billedClockInAt: string, billedClockOutAt: string}>} records - Array of time records
 * @returns {number} Total hours as floating point number
 */
export const calculateTotalHours = (records) => {
  if (!Array.isArray(records) || records.length === 0) return 0;

  return roundTo2Decimals(
    records.reduce((total, record) => {
      const start = dayjs(record.billedClockInAt);
      const end = dayjs(record.billedClockOutAt);
      const hours = end.diff(start, 'hour', true);
      return total + hours;
    }, 0)
  );
};

/**
 * Calculates tax amount based on sum and tax-free sum
 * @param {number} sum - Total sum including tax
 * @param {number} taxFreeSum - Total sum excluding tax
 * @returns {number} Tax amount rounded to 2 decimals
 */
const calculateTaxAmount = (sum, taxFreeSum) => {
  const safeSum = safeNumber(sum);
  const safeTaxFreeSum = safeNumber(taxFreeSum);

  const taxAmount = safeSum - safeTaxFreeSum;
  return roundTo2Decimals(taxAmount);
};

/**
 * Calculates invoice row values with proper validation
 * @param {number|null} [unitPrice] - Price per unit (defaults to 0 if invalid)
 * @param {number|null} [quantity] - Quantity (defaults to 0 if invalid)
 * @param {number|null} [vatPercentage] - VAT percentage (clamped between 0-100)
 * @param {number|null} [discountPercentage] - Discount percentage (clamped between 0-100)
 * @param {number|null} [overheadPercentage] - Overhead percentage (clamped between 0-100)
 * @returns {{taxFreeSum: number, taxAmount: number, sum: number}} Result with values rounded to 2 decimals
 */
export const calculateInvoiceRowValues = (
  unitPrice = 0,
  quantity = 0,
  vatPercentage = 0,
  discountPercentage = 0,
  overheadPercentage = 0
) => {
  try {
    // Convert inputs to safe numbers
    const safeUnitPrice = Math.max(safeNumber(unitPrice), 0);
    const safeQuantity = Math.max(safeNumber(quantity), 0);
    const safeVatPercentage = normalizePercentage(vatPercentage);
    const safeDiscountPercentage = normalizePercentage(discountPercentage);
    const safeOverheadPercentage = normalizePercentage(overheadPercentage);

    const priceWithDiscount = roundTo2Decimals(
      safeUnitPrice * (1 - safeDiscountPercentage / 100)
    );

    const priceWithOverhead = roundTo2Decimals(
      priceWithDiscount * (1 + safeOverheadPercentage / 100)
    );

    const taxFreeSum = roundTo2Decimals(priceWithOverhead * safeQuantity);
    const taxAmount = roundTo2Decimals(taxFreeSum * (safeVatPercentage / 100));
    const sum = roundTo2Decimals(taxFreeSum + taxAmount);

    return {
      taxFreeSum,
      taxAmount,
      sum,
    };
  } catch (error) {
    console.error('Error in calculateInvoiceRowValues:', error);
    // Return safe default values if anything goes wrong
    return {
      taxFreeSum: 0,
      taxAmount: 0,
      sum: 0,
    };
  }
};

/**
 * Calculates invoice totals from an array of invoice rows
 * @param {Array<{taxFreeSum: number, taxAmount: number, sum: number}>} rows - Array of invoice rows
 * @returns {{taxFreeSum: number, taxAmount: number, sum: number}} Totals rounded to 2 decimals
 */
export const calculateInvoiceTotalsFromRows = (rows) => {
  if (!Array.isArray(rows) || rows.length === 0) {
    return {
      taxFreeSum: 0,
      taxAmount: 0,
      sum: 0,
    };
  }

  return rows.reduce(
    (acc, row) => ({
      taxFreeSum: roundTo2Decimals(acc.taxFreeSum + (row.taxFreeSum || 0)),
      taxAmount: roundTo2Decimals(acc.taxAmount + (row.taxAmount || 0)),
      sum: roundTo2Decimals(acc.sum + (row.sum || 0)),
    }),
    {
      taxFreeSum: 0,
      taxAmount: 0,
      sum: 0,
    }
  );
};

/**
 * Calculates additional work totals from rows.
 * Reuses invoice row totals calculation and extends it with overhead total.
 * @param {Array<{taxFreeSum: number, taxAmount: number, sum: number, overheadAmount?: number}>} rows
 * @returns {{taxFreeSum: number, taxAmount: number, sum: number, overhead: number}}
 */
export const calculateAdditionalWorkTotals = (rows) => {
  const baseTotals = calculateInvoiceTotalsFromRows(rows);

  if (!Array.isArray(rows) || rows.length === 0) {
    return {
      ...baseTotals,
      overhead: 0,
    };
  }

  const overhead = rows.reduce((total, row) => {
    const hasExplicitOverhead = row.overheadAmount !== undefined;

    const calculatedOverhead = hasExplicitOverhead
      ? safeNumber(row.overheadAmount)
      : safeNumber(row.taxFreeSum) -
        safeNumber(
          calculateInvoiceRowValues(
            row.unitPrice,
            row.quantity,
            0,
            row.discountPercentage,
            0
          )?.taxFreeSum
        );

    return roundTo2Decimals(total + calculatedOverhead);
  }, 0);

  return {
    ...baseTotals,
    overhead,
  };
};

/**
 * Calculates invoice totals from an array of invoices
 * @param {Array<{sum: number, sum_tax: number}>} invoices - Array of invoices
 * @returns {{taxFreeSum: number, taxAmount: number, sum: number}} Totals rounded to 2 decimals
 */
export const calculateInvoiceTotals = (invoices) => {
  if (!Array.isArray(invoices) || invoices.length === 0) {
    return {
      taxFreeSum: 0,
      taxAmount: 0,
      sum: 0,
    };
  }

  invoices = invoices.filter(
    (invoice) => invoice.approvalStatus !== 'REJECTED'
  );

  const invoiceTotals = invoices.reduce(
    (acc, invoice) => {
      const taxFreeSum = safeNumber(invoice.sum) || 0;
      const taxAmount = calculateTaxAmount(invoice.sum_tax, invoice.sum || 0);
      const noTax = invoice.sum === invoice.sum_tax;
      const sum = noTax
        ? safeNumber(invoice.sum_tax) * 1.255 || 0
        : safeNumber(invoice.sum_tax) || 0;
      return {
        taxFreeSum: roundTo2Decimals(acc.taxFreeSum + taxFreeSum),
        taxAmount: roundTo2Decimals(acc.taxAmount + taxAmount),
        sum: roundTo2Decimals(acc.sum + sum),
      };
    },
    {
      taxFreeSum: 0,
      taxAmount: 0,
      sum: 0,
    }
  );

  return invoiceTotals;
};

/**
 * Calculates saleInvoice totals from an array of saleInvoices
 * @param {Array<{sum: number, sum_tax: number}>} saleInvoices - Array of saleInvoices
 * @returns {{taxFreeSum: number, taxAmount: number, sum: number}} Totals rounded to 2 decimals
 */
export const calculateSaleInvoiceTotals = (saleInvoices) => {
  if (!Array.isArray(saleInvoices) || saleInvoices.length === 0) {
    return {
      taxFreeSum: 0,
      taxAmount: 0,
      sum: 0,
    };
  }

  const invoiceTotals = saleInvoices.reduce(
    (acc, invoice) => {
      const taxFreeSum = safeNumber(invoice.taxFreeSum) || 0;
      const taxAmount = calculateTaxAmount(
        invoice.sum,
        invoice.taxFreeSum || 0
      );
      const noTax = invoice.taxFreeSum === invoice.sum;
      const sum = noTax
        ? safeNumber(invoice.sum) * 1.255 || 0
        : safeNumber(invoice.sum) || 0;
      return {
        taxFreeSum: roundTo2Decimals(acc.taxFreeSum + taxFreeSum),
        taxAmount: roundTo2Decimals(acc.taxAmount + taxAmount),
        sum: roundTo2Decimals(acc.sum + sum),
      };
    },
    {
      taxFreeSum: 0,
      taxAmount: 0,
      sum: 0,
    }
  );

  return invoiceTotals;
};

export const calculateInvoiceTotalsFromSaleInvoices = (
  invoices = [],
  saleInvoices = [],
  otherCosts = 0
) => {
  const invoiceTotals = calculateInvoiceTotals(invoices);
  const saleInvoiceTotals = calculateSaleInvoiceTotals(saleInvoices);
  const safeOtherCosts = safeNumber(otherCosts);

  return {
    taxFreeSum: roundTo2Decimals(
      saleInvoiceTotals.taxFreeSum - (invoiceTotals.taxFreeSum + safeOtherCosts)
    ),
    taxAmount: roundTo2Decimals(
      saleInvoiceTotals.taxAmount - (invoiceTotals.taxAmount + safeOtherCosts)
    ),
    sum: roundTo2Decimals(
      saleInvoiceTotals.sum - (invoiceTotals.sum + safeOtherCosts)
    ),
  };
};

/**
 * Calculates assignment costs based on work cost and invoice totals
 * @param {Object} assignment - Assignment object containing workCost
 * @param {Array} invoices - Array of invoice objects
 * @returns {{taxFreeSum: number, taxAmount: number, sum: number}} Totals rounded to 2 decimals
 */
export const calculateAssignmentCosts = (assignment, invoices) => {
  const {
    workCost,
    otherCost,
    additionalWorkTaxFreeSum,
    additionalWorkTaxAmount,
    additionalWorkTotalSum,
  } = assignment;
  const safeWorkCost = safeNumber(workCost);
  const safeOtherCost = safeNumber(otherCost);
  const safeAdditionalWorkTaxFreeSum = safeNumber(additionalWorkTaxFreeSum);
  const safeAdditionalWorkTaxAmount = safeNumber(additionalWorkTaxAmount);
  const safeAdditionalWorkTotalSum = safeNumber(additionalWorkTotalSum);
  const {
    taxFreeSum: invoicesTaxFreeSum,
    taxAmount: invoicesTaxAmount,
    sum: invoicesSum,
  } = calculateInvoiceTotals(invoices);

  const totals = {
    taxFreeSum: roundTo2Decimals(
      invoicesTaxFreeSum +
        safeWorkCost +
        safeOtherCost +
        safeAdditionalWorkTaxFreeSum
    ),
    taxAmount: roundTo2Decimals(
      invoicesTaxAmount + safeAdditionalWorkTaxAmount
    ),
    sum: roundTo2Decimals(
      invoicesSum +
        safeWorkCost +
        safeOtherCost +
        (safeAdditionalWorkTotalSum || safeAdditionalWorkTaxFreeSum)
    ),
  };
  return totals;
};
